import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sets the document title using an array of title segments that are joined with " | "
 * Each segment will be translated before joining and 'Alaska' is automatically appended
 *
 * @param {string[]} title - Array of title segments, ordered from most specific to most general
 *                          e.g. ['Search Keys', 'Account'] becomes "Search Keys | Account | Alaska"
 * @param {string} defaultTitle - Title to use when no segments are provided or as the base title. Default is 'Alaska'
 *
 * @example
 * // Sets title to "John Smith | Account | Alaska"
 * usePageTitle([accountName, 'Account'])
 *
 * // Sets title to "Search Keys | Alaska"
 * usePageTitle(['Search Keys'])
 */
const usePageTitle = (title: string[], defaultTitle = 'Alaska') => {
  // we don't expose the setter so we can format the title segments on set
  const [pageTitle, setInternalPageTitle] = useState(title);
  const { t } = useTranslation();

  const formatPageTitle = useCallback(
    (titleArray: string[]) => {
      const translatedArray = titleArray.map((segment) => t(segment));
      return `${translatedArray.join(' | ')} | Alaska`;
    },
    [t]
  );

  const setPageTitle = useCallback((newTitle: string[]) => {
    setInternalPageTitle(newTitle);
  }, []);

  useEffect(() => {
    document.title = formatPageTitle(pageTitle);
    return () => {
      document.title = defaultTitle;
    };
  }, [defaultTitle, pageTitle, formatPageTitle]);

  return [pageTitle, setPageTitle] as [string[], (newTitle: string[]) => void];
};

export { usePageTitle };
